import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import { createResource, listResource } from "@/lib/master-data";

type DataType = "brand" | "os" | "office" | "asset_option";

interface BasicDataRow {
  id: string;
  dataType: string;
  category: string | null;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

const TABS: { key: DataType; label: string; description: string }[] = [
  { key: "brand", label: "品牌", description: "资产品牌，例如联想、戴尔、苹果。" },
  { key: "os", label: "操作系统", description: "Windows、macOS、Linux 等。" },
  { key: "office", label: "Office 软件", description: "Office 365、WPS 等。" },
  { key: "asset_option", label: "资产字段选项", description: "设备小问题、资产类型等可维护选项。" },
];

export function SettingsMasterDataPage() {
  const [tab, setTab] = useState<DataType>("brand");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", category: "", isActive: true });
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const active = TABS.find((item) => item.key === tab) ?? (TABS[0] as (typeof TABS)[number]);
  const query = useQuery({
    queryKey: ["basic-data", tab],
    queryFn: () => listResource<BasicDataRow>(`/api/v1/admin/basic-data/${tab}`),
  });

  async function create() {
    if (!form.name.trim()) {
      toast.error("请输入名称");
      return;
    }
    setSaving(true);
    try {
      await createResource(`/api/v1/admin/basic-data/${tab}`, {
        dataType: tab,
        name: form.name.trim(),
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
        ...(form.category.trim() ? { category: form.category.trim() } : {}),
        isActive: form.isActive,
      });
      toast.success("基础数据已创建");
      setOpen(false);
      setForm({ name: "", code: "", category: "", isActive: true });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["basic-data", tab] }),
        queryClient.invalidateQueries({ queryKey: ["reference-data"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "创建失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="主数据与基础数据"
        description="维护品牌、操作系统、Office 软件和资产字段选项；已引用的选项只停用、不物理删除。"
        actions={
          <>
            <Button variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw className={query.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              刷新
            </Button>
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" />
              新增{active.label}
            </Button>
          </>
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as DataType)}>
        <TabsList className="flex-wrap">
          {TABS.map((item) => (
            <TabsTrigger key={item.key} value={item.key}>
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>{active.label}</CardTitle>
          <CardDescription>{active.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="space-y-2">
              {["m1", "m2", "m3", "m4"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : (query.data ?? []).length === 0 ? (
            <EmptyState title={`还没有${active.label}`} description="点击右上角新增第一条选项。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>编码</TableHead>
                  <TableHead>名称</TableHead>
                  <TableHead>分类</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(query.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">{row.code}</TableCell>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{row.category ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={row.isActive ? "success" : "secondary"}>
                        {row.isActive ? "启用" : "停用"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增{active.label}</DialogTitle>
            <DialogDescription>编码留空时由系统自动生成。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>名称</Label>
              <Input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>编码（选填）</Label>
              <Input
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
              />
            </div>
            {tab === "asset_option" ? (
              <div className="space-y-2">
                <Label>字段分类（选填，例如 minor_issue）</Label>
                <Input
                  value={form.category}
                  onChange={(event) => setForm({ ...form, category: event.target.value })}
                />
              </div>
            ) : null}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                className="h-4 w-4"
              />
              启用
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button onClick={create} disabled={saving}>
              {saving ? "保存中…" : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
